import { bufferToBase64URLString, sendSignal } from "@simplewebauthn/browser";
import { ViewerPasskeySignalsDocument } from "~/gql/graphql";

export function usePasskeySignals() {
	const { resolveClient } = useApolloClient();
	const { reportError } = useErrorReporter();

	/**
	 * Tells the browser's passkey provider (iCloud Keychain, Google Password Manager, …) which of the viewer's passkeys
	 * the server still accepts, and what the account is called now. A passkey deleted from the viewer's passkeys page
	 * then stops being offered at sign-in, and a changed name or phone number replaces the one saved at registration.
	 *
	 * Call it after every sign-in, once any passkey created as part of that sign-in has been verified: the accepted list
	 * is read afterwards, and a passkey missing from it gets hidden. Don't await it. Signals are best-effort, Firefox has
	 * no Signal API, and Safari 26 can leave the promise pending.
	 *
	 * @param rpID the RP ID from the server's WebAuthn options, which is the one its passkeys are registered under
	 */
	async function sendPasskeySignals(rpID: string | undefined): Promise<void> {
		if (!rpID || typeof PublicKeyCredential === "undefined") return;

		const canSignalAccepted = typeof PublicKeyCredential.signalAllAcceptedCredentials === "function";
		const canSignalDetails = typeof PublicKeyCredential.signalCurrentUserDetails === "function";

		if (!canSignalAccepted && !canSignalDetails) return;

		try {
			const { data } = await resolveClient().query({
				query: ViewerPasskeySignalsDocument,
				fetchPolicy: "network-only",
			});
			const viewer = data?.viewer;

			if (!viewer) return;

			// the server registers passkeys with the UTF-8 bytes of the viewer's dashless UUID (its `oid`) as the user handle
			const userID = bufferToBase64URLString(new TextEncoder().encode(viewer.oid).buffer);
			const signals: Promise<undefined>[] = [];

			// the connection is capped at 100 per page, so a longer list can't be sent whole, and a partial one would hide
			// every passkey left off it
			const { edges, totalCount } = viewer.publicKeyCredentials;
			const credentialIDs = edges?.flatMap((edge) => edge?.node ? [edge.node.credentialId] : []) ?? [];

			if (canSignalAccepted && credentialIDs.length === totalCount) {
				signals.push(sendSignal({
					signalName: "allAcceptedCredentials",
					rpID,
					userID,
					allAcceptedCredentialIDs: credentialIDs,
				}));
			}

			// mirrors the names the server registers: the phone number, displayed as the client's or agent's name if any
			if (canSignalDetails && viewer.accountPhone) {
				signals.push(sendSignal({
					signalName: "currentUserDetails",
					rpID,
					userID,
					userName: viewer.accountPhone,
					userDisplayName: viewer.__typename === "BasicUser" ? viewer.accountPhone : viewer.name,
				}));
			}

			for (const signal of signals) {
				signal.catch((e: unknown) => reportError(e, "Failed to send passkey signal:"));
			}
		} catch (e) {
			reportError(e, "Failed to send passkey signals:");
		}
	}

	return {
		sendPasskeySignals,
	};
}
